import { loadAppleProducts } from './appleIap';
import { appleProductIdForPlan, type AppleIapPlan } from './appleIapProducts';
import { PRO_TIER_NAME } from './productTier';
import { isNativeIOS } from './platform';

export type SubscriptionOfferDisplay = {
  plan: AppleIapPlan;
  /** Product / subscription title shown to the user */
  title: string;
  /** Most conspicuous billed amount, e.g. "$14.99" */
  priceLabel: string;
  /** e.g. "1 month" */
  periodLabel: string;
  /** e.g. "$14.99/month" */
  pricePerPeriodLabel: string;
  /** Services included each billing period */
  services: string[];
  /** Auto-renew disclosure */
  renewNote: string;
};

/** Fallbacks if StoreKit has not returned metadata yet (or web Stripe). */
const FALLBACK: Record<
  AppleIapPlan,
  { priceLabel: string; periodLabel: string; title: string; services: string[] }
> = {
  coach: {
    title: `${PRO_TIER_NAME} Monthly`,
    priceLabel: '$7.99',
    periodLabel: '1 month',
    services: [
      'AI coaching chat grounded in your logged training, nutrition, and recovery',
      'Form check video analysis',
      'Progress & recovery insights',
      'Ad-free experience (no footer partner picks)',
    ],
  },
  adfree: {
    title: 'Ad-Free Monthly',
    priceLabel: '$2.99',
    periodLabel: '1 month',
    services: ['Removes partner gear picks throughout the app'],
  },
};

function periodFromUnits(numberOfUnits: number, unit: string): string {
  const u = unit.toLowerCase();
  const n = Math.max(1, numberOfUnits || 1);
  if (u.startsWith('day')) return n === 1 ? '1 day' : `${n} days`;
  if (u.startsWith('week')) return n === 1 ? '1 week' : `${n} weeks`;
  if (u.startsWith('month')) return n === 1 ? '1 month' : `${n} months`;
  if (u.startsWith('year')) return n === 1 ? '1 year' : `${n} years`;
  return '1 month';
}

function perPeriodSuffix(periodLabel: string): string {
  if (periodLabel === '1 month') return '/month';
  if (periodLabel === '1 week') return '/week';
  if (periodLabel === '1 year') return '/year';
  if (periodLabel === '1 day') return '/day';
  return ` / ${periodLabel}`;
}

export function fallbackSubscriptionOffer(plan: AppleIapPlan): SubscriptionOfferDisplay {
  const f = FALLBACK[plan];
  return {
    plan,
    title: f.title,
    priceLabel: f.priceLabel,
    periodLabel: f.periodLabel,
    pricePerPeriodLabel: `${f.priceLabel}${perPeriodSuffix(f.periodLabel)}`,
    services: f.services,
    renewNote:
      'Payment is charged to your Apple ID. The subscription renews automatically unless canceled at least 24 hours before the end of the current period. Manage or cancel in Settings → Apple ID → Subscriptions.',
  };
}

/** Load localized StoreKit price/period for a plan (iOS). Falls back to static copy. */
export async function loadSubscriptionOffer(plan: AppleIapPlan): Promise<SubscriptionOfferDisplay> {
  const base = fallbackSubscriptionOffer(plan);
  if (!isNativeIOS()) return base;

  try {
    const products = await loadAppleProducts();
    const productId = appleProductIdForPlan(plan);
    const product = products.find((p) => p.id === productId);
    if (!product?.displayPrice) return base;

    const periodLabel = product.subscriptionPeriod
      ? periodFromUnits(product.subscriptionPeriod.numberOfUnits, product.subscriptionPeriod.unit)
      : base.periodLabel;
    const priceLabel = product.displayPrice;
    return {
      ...base,
      title: product.displayName?.trim() || base.title,
      priceLabel,
      periodLabel,
      pricePerPeriodLabel: `${priceLabel}${perPeriodSuffix(periodLabel)}`,
    };
  } catch {
    return base;
  }
}
